/**
 * Typed TanStack Query v5 hooks over the Watchtower Console read API.
 *
 * The interfaces here mirror the query Lambda verbatim — they ARE the contract:
 *   - lib/lambdas/query.ts (runsList / eventsDrillIn / logsDrillIn)
 *
 * Routes (all GET; the console is read-only):
 *   GET /v1/runs?from=&to=[&platform=][&<labelKey>=<val>…]  → { runs }
 *   GET /v1/runs/{platform}/{session_id}/events            → { events }
 *   GET /v1/runs/{platform}/{session_id}/logs?severity=&limit=&cursor=
 *                                                          → { logs, next_cursor }
 */

import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './auth';
import { ApiError, makeApi, type Api } from './api';

// ---------------------------------------------------------------------------
// Response shapes — mirror lib/lambdas/query.ts verbatim.
// ---------------------------------------------------------------------------

/** A per-run rollup from GET /v1/runs (query.ts runsList). `models` is the set
 *  of opaque model identifiers seen across the run's turns. `labels` are opaque
 *  correlation labels the service never interprets. */
export interface Run {
  session_id?: string;
  platform: string;
  run_date: string;
  models: string[];
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: number;
  turn_count: number;
  log_count?: number;
  error_count?: number;
  first_seen?: string;
  last_seen?: string;
  env?: string;
  labels: Record<string, string>;
}

export interface RunsResponse {
  runs: Run[];
}

/** A per-turn EVENT row from GET /v1/runs/{platform}/{session_id}/events
 *  (query.ts eventsDrillIn), ascending by SK. */
export interface TurnEvent {
  sk: string;
  session_id: string;
  platform: string;
  model: string;
  env: string;
  ts_iso: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: number;
}

export interface EventsResponse {
  events: TurnEvent[];
}

/** A general (non-api_request) log record from GET
 *  /v1/runs/{platform}/{session_id}/logs (query.ts logsDrillIn). `body`/`attrs`
 *  are opaque; the `*_truncated` flags mark values the receiver clipped. */
export interface LogLine {
  ts_iso: string;
  severity: string;
  body: unknown;
  event_name?: string;
  attrs?: Record<string, unknown>;
  session_id: string;
  platform: string;
  env?: string;
  seq: number;
  body_truncated?: boolean;
  attrs_truncated?: boolean;
}

export interface LogsResponse {
  logs: LogLine[];
  /** Opaque base64url cursor for the next page, or null when exhausted. */
  next_cursor: string | null;
}

// ---------------------------------------------------------------------------
// Api context — built from useAuth().
// ---------------------------------------------------------------------------

const ApiContext = createContext<Api | null>(null);

/** Provides the Api instance, wired to the auth layer's `ensureFreshToken`.
 *  The client awaits that getter before every request, so each request carries
 *  a token guaranteed valid for ≥60s — silently refreshing first if the current
 *  one is missing/expiring — or an empty header once the session is dead. */
export function ApiProvider({ children }: { children: ReactNode }) {
  const { config, ensureFreshToken, signOut, denyAccess } = useAuth();
  const api = useMemo<Api>(
    // `ensureFreshToken` is re-created every AuthProvider render, so this
    // rebuilds `makeApi` each render — cheap, and it guarantees we never capture
    // a stale closure. The getter itself reads live sessionStorage, so a rebuilt
    // instance is always correct.
    //
    // Wrap `get` so the spec §5 security contract holds on the data-fetch path:
    // an API 401 clears the session (→ sign-in), a 403 latches NoAccess. The
    // error is rethrown so react-query still surfaces the failure downstream.
    () => {
      const base = makeApi(config, ensureFreshToken);
      return {
        get: async <T,>(path: string): Promise<T> => {
          try {
            return await base.get<T>(path);
          } catch (e) {
            if (e instanceof ApiError) {
              if (e.status === 401) signOut();
              else if (e.status === 403) denyAccess();
            }
            throw e;
          }
        },
      };
    },
    [config, ensureFreshToken, signOut, denyAccess],
  );
  return createElement(ApiContext.Provider, { value: api }, children);
}

export function useApi(): Api {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi must be used within an ApiProvider');
  return api;
}

// ---------------------------------------------------------------------------
// Read hooks.
// ---------------------------------------------------------------------------

/** GET /v1/runs?from=&to=[&platform=][&k=v…] → per-run rollups over [from, to]
 *  (inclusive), narrowed by an optional platform and opaque label filters.
 *  Gated by `enabled` so nothing fires until both date bounds are set. */
export function useRuns(
  from: string,
  to: string,
  platform?: string,
  labelFilters?: Record<string, string>,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['runs', from, to, platform ?? '', labelFilters ?? {}] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('from', from);
      params.set('to', to);
      if (platform) params.set('platform', platform);
      for (const [k, v] of Object.entries(labelFilters ?? {})) params.set(k, v);
      return api.get<RunsResponse>(`/v1/runs?${params.toString()}`);
    },
    enabled: Boolean(from && to),
  });
}

/** GET /v1/runs/{platform}/{session_id}/events → the per-turn EVENT drill-in
 *  for one run, ascending by SK. */
export function useTurns(platform: string, sessionId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['events', platform, sessionId] as const,
    queryFn: () =>
      api.get<EventsResponse>(
        `/v1/runs/${encodeURIComponent(platform)}/${encodeURIComponent(sessionId)}/events`,
      ),
    enabled: Boolean(platform && sessionId),
  });
}

/** GET /v1/runs/{platform}/{session_id}/logs?severity=&limit=200[&cursor=] →
 *  the general-log drill-in for one run, paged. `severity` is a FLOOR filter.
 *  `cursor` is the opaque next_cursor returned by the previous page. */
export function useLogs(platform: string, sessionId: string, severity: string) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['logs', platform, sessionId, severity] as const,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set('severity', severity);
      params.set('limit', '200');
      if (pageParam) params.set('cursor', pageParam);
      return api.get<LogsResponse>(
        `/v1/runs/${encodeURIComponent(platform)}/${encodeURIComponent(sessionId)}/logs?${params.toString()}`,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(platform && sessionId),
  });
}

// ---------------------------------------------------------------------------
// Root provider: QueryClientProvider + ApiProvider.
// ---------------------------------------------------------------------------

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
    },
  });
}

export function QueryProvider({
  client,
  children,
}: {
  client: QueryClient;
  children: ReactNode;
}) {
  return createElement(
    QueryClientProvider,
    { client },
    createElement(ApiProvider, null, children),
  );
}
